import { useFlags } from "launchdarkly-react-client-sdk";
import React, { Suspense, useState } from "react";

import Button from "@mui/material/Button";

import Dialog from "components/dialog";
import { DIRECT_SCANNING, DIRECT_SCANNING_V2 } from "constants/feature-flags";
import { ScanLoadErrorBoundary } from "features/dynamic-web-twain/scan-load-error-boundary.component";
import { ScanLoadingFallback } from "features/dynamic-web-twain/scan-load-status.component";
import { EDocumentsSourceType } from "services/types/patient-e-documents.type";
import { lazyWithRetry } from "utils/lazy-with-retry.util";

// DEV-2806. Both branches are lazy so the 1MB Dynamsoft SDK leaves the entry
// bundle: `patient-header/avatar` imports `photo-picker`, which imports this
// modal, so a static import here put the whole scanner in app-shell code — 295 kB
// gzip preloaded on first paint for every user, on every route, whether or not
// they ever scan. It now arrives when the dialog opens.
//
// Both are wrapped in the escalating fallback + error boundary below, so a chunk
// that 404s or hangs surfaces something actionable instead of an endless spinner.
// That protection is deliberately OUTSIDE the flag: it applies to V1 too.
//
// ENG-1867. lazyWithRetry gives a deploy-invalidated chunk one cache-busted
// retry before failing (58% of stale-build chunk failures were this dialog).
// `reloadFallback: false` because an auto-reload here can be vetoed by the
// Ambient Scribe's beforeunload guard — a vetoed reload parks the lazy on a
// never-settling promise for the tab's lifetime, the exact hang DEV-2806
// removed. A final failure still lands in ScanLoadErrorBoundary below, whose
// panel offers the (recording-aware) reload.
const DynamicWebTwain = lazyWithRetry(
  () => import("./dynamic-web-twain.component"),
  "dynamic-web-twain",
  { reloadFallback: false }
);
const DynamicWebTwainV2 = lazyWithRetry(
  () => import("features/dynamic-web-twain-v2/dynamic-web-twain.component"),
  "dynamic-web-twain-v2",
  { reloadFallback: false }
);

export type FolderSettings = {
  id: number;
  type: EDocumentsSourceType;
  folderId: string;
  defaultFolderDestinationPath: string;
};

type DynamicWebTwainModalProps = {
  submitButtonText: string;
  multiplePages?: boolean;
  title: string;
  folderSettings?: FolderSettings;
  onSave?: (file: File) => void;
  onUploadSuccess?: (fileName: string) => void;
};

const DynamicWebTwainModal: React.FC<DynamicWebTwainModalProps> = ({
  multiplePages,
  title,
  folderSettings,
  submitButtonText,
  onSave,
  onUploadSuccess
}) => {
  const flags = useFlags();
  const allowDirectScanning = flags[DIRECT_SCANNING];

  const [isOpen, setIsOpen] = useState(false);
  // DEV-2806. LaunchDarkly streams flag updates, so re-reading this on every render
  // would let a mid-session flip swap the implementation UNDER AN OPEN DIALOG,
  // destroying an in-progress scan and its buffered images — exactly what happens
  // while we roll the flag out. Latch the value at open; V1/V2 never coexist.
  const [useV2, setUseV2] = useState(false);

  const onClose = () => {
    setIsOpen(false);
  };

  const onOpen = () => {
    setUseV2(Boolean(flags[DIRECT_SCANNING_V2]));
    setIsOpen(true);
  };

  // Gate the trigger, not the whole component. `return null` here ran on every
  // render, so an LD flip of `directScanning` (kill-switch, targeting edit) while
  // the dialog was open tore it out mid-scan with no prompt and no close animation
  // — the same hazard `useV2` is latched against, one level up.
  if (!allowDirectScanning && !isOpen) {
    return null;
  }

  const scanProps = {
    onClose,
    multiplePages,
    folderSettings,
    submitButtonText,
    onSave,
    onUploadSuccess
  };

  return (
    <>
      {allowDirectScanning && <Button onClick={onOpen}>Scan</Button>}
      <Dialog
        title={title}
        open={isOpen}
        onClose={onClose}
        maxWidth="lg"
        dialogContentProps={{ sx: { pb: 5 } }}
      >
        <ScanLoadErrorBoundary>
          <Suspense fallback={<ScanLoadingFallback chunkName="scanner" />}>
            {useV2 ? (
              <DynamicWebTwainV2 {...scanProps} />
            ) : (
              <DynamicWebTwain {...scanProps} />
            )}
          </Suspense>
        </ScanLoadErrorBoundary>
      </Dialog>
    </>
  );
};

export default DynamicWebTwainModal;
